import { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Loader2, Plus, Pencil, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  useProposalPresets,
  useCreatePreset,
  useUpdatePreset,
  useDeletePreset,
  EstimateProposalPreset,
  SEED_PRESETS,
} from "@/hooks/useEstimateProposalPresets";

const JOB_TYPES = [
  { value: "", label: "All / Default" },
  { value: "flooring", label: "Flooring" },
  { value: "roofing", label: "Roofing" },
  { value: "emergency", label: "Emergency Response" },
  { value: "general", label: "General Construction" },
  { value: "other", label: "Other" },
];

export const EstimateProposalPresetsSection = () => {
  const { data: presets = [], isLoading } = useProposalPresets();
  const createPreset = useCreatePreset();
  const updatePreset = useUpdatePreset();
  const deletePreset = useDeletePreset();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<EstimateProposalPreset | null>(null);

  // Form state
  const [name, setName] = useState("");
  const [jobType, setJobType] = useState("");
  const [clarifications, setClarifications] = useState("");
  const [exclusions, setExclusions] = useState("");
  const [acceptanceText, setAcceptanceText] = useState("");
  const [isDefault, setIsDefault] = useState(false);

  const openNew = () => {
    setEditing(null);
    setName("");
    setJobType("");
    setClarifications("");
    setExclusions("");
    setAcceptanceText("");
    setIsDefault(false);
    setDialogOpen(true);
  };

  const openEdit = (preset: EstimateProposalPreset) => {
    setEditing(preset);
    setName(preset.name);
    setJobType(preset.job_type || "");
    setClarifications(preset.clarifications.map((c) => `• ${c}`).join("\n"));
    setExclusions(preset.exclusions.map((e) => `• ${e}`).join("\n"));
    setAcceptanceText(preset.acceptance_text || "");
    setIsDefault(preset.is_default);
    setDialogOpen(true);
  };

  const parseList = (text: string): string[] =>
    text
      .split(/\r?\n/)
      .map((l) => l.replace(/^\s*[-*•·]\s*/, "").trim())
      .filter((l) => l.length > 0);

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");

      const payload = {
        company_id: companyId,
        name: name.trim(),
        job_type: jobType || null,
        clarifications: parseList(clarifications),
        exclusions: parseList(exclusions),
        acceptance_text: acceptanceText.trim() || null,
        is_default: isDefault,
      };

      if (editing) {
        await updatePreset.mutateAsync({ id: editing.id, ...payload });
        toast.success("Preset updated");
      } else {
        await createPreset.mutateAsync(payload);
        toast.success("Preset created");
      }
      setDialogOpen(false);
    } catch (err: any) {
      toast.error(err.message || "Failed to save preset");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deletePreset.mutateAsync(id);
      toast.success("Preset deleted");
    } catch (err: any) {
      toast.error(err.message || "Failed to delete");
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");
      for (const seed of SEED_PRESETS) {
        const exists = presets.find((p) => p.name === seed.name);
        if (!exists) {
          await createPreset.mutateAsync({ ...seed, company_id: companyId });
        }
      }
      toast.success("Default presets loaded");
    } catch (err: any) {
      toast.error(err.message || "Failed to seed presets");
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Estimate Proposal Presets</CardTitle>
              <CardDescription>
                Configure clarifications, exclusions, and acceptance text for your estimates. Create presets per job type.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {presets.length === 0 && (
                <Button variant="outline" size="sm" onClick={handleSeedDefaults} disabled={createPreset.isPending}>
                  Load Defaults
                </Button>
              )}
              <Button size="sm" onClick={openNew}>
                <Plus className="h-4 w-4 mr-1" /> New Preset
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Job Type</TableHead>
                <TableHead className="text-center">Clarifications</TableHead>
                <TableHead className="text-center">Exclusions</TableHead>
                <TableHead className="text-center">Default</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : presets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No presets yet. Click "Load Defaults" to get started.
                  </TableCell>
                </TableRow>
              ) : (
                presets.map((preset) => (
                  <TableRow key={preset.id}>
                    <TableCell className="font-medium">{preset.name}</TableCell>
                    <TableCell>
                      {preset.job_type ? (
                        <Badge variant="outline" className="text-xs">{preset.job_type}</Badge>
                      ) : (
                        <span className="text-muted-foreground text-xs">All</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">{preset.clarifications.length}</TableCell>
                    <TableCell className="text-center">{preset.exclusions.length}</TableCell>
                    <TableCell className="text-center">
                      {preset.is_default && <Star className="h-4 w-4 text-yellow-500 mx-auto fill-yellow-500" />}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(preset)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(preset.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-[700px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Preset" : "New Preset"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Flooring Default" />
              </div>
              <div>
                <Label>Job Type</Label>
                <Select value={jobType} onValueChange={setJobType}>
                  <SelectTrigger><SelectValue placeholder="All / Default" /></SelectTrigger>
                  <SelectContent>
                    {JOB_TYPES.map((jt) => (
                      <SelectItem key={jt.value} value={jt.value || "all"}>{jt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="rounded border-border"
              />
              <Label className="text-sm">Set as default preset (used when no job-type match)</Label>
            </div>

            <Separator />

            <div>
              <Label>Clarifications & Assumptions</Label>
              <p className="text-xs text-muted-foreground mb-1">One per line. Bullet markers (•, -, *) are stripped automatically.</p>
              <Textarea
                value={clarifications}
                onChange={(e) => setClarifications(e.target.value)}
                rows={6}
                placeholder="• Pricing based on work performed during normal business hours..."
              />
            </div>

            <div>
              <Label>Exclusions</Label>
              <p className="text-xs text-muted-foreground mb-1">One per line.</p>
              <Textarea
                value={exclusions}
                onChange={(e) => setExclusions(e.target.value)}
                rows={6}
                placeholder="• Permits, fees, and plan review costs..."
              />
            </div>

            <div>
              <Label>Acceptance Text</Label>
              <p className="text-xs text-muted-foreground mb-1">Shown above the signature lines. Leave blank to use company default.</p>
              <Textarea
                value={acceptanceText}
                onChange={(e) => setAcceptanceText(e.target.value)}
                rows={4}
                placeholder="The undersigned accepts this Proposal..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={createPreset.isPending || updatePreset.isPending}>
              {(createPreset.isPending || updatePreset.isPending) && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
